// Copyright 2026, Imprivata, Inc.  All rights reserved.

export enum AuthnLevel {
  ICP_SINGLE_FA = 'icp-single-fa',
  ICP_MFA = 'icp-mfa',
  ICP_PASSWORDLESS_MFA = 'icp-passwordless-mfa',
  ICP_MFA_IF_ENROLLED = 'icp-mfa-if-enrolled',
}

// AM_PORTAL shares the IAM instance ID on the server; the two names are kept
// distinct so call sites signal which domain (auth settings vs. portal admin
// settings) they target.
export enum InstanceKey {
  USS = 'feb45631-a492-4a1a-8297-417da1347bd5',
  IAM = '4c44699a-fc78-4b9e-8975-557d26f08bef',
  AM_PORTAL = '4c44699a-fc78-4b9e-8975-557d26f08bef',
}

export enum UserCanShowLiveFeedValue {
  ON = 'on',
  OFF = 'off',
}

export enum DiscardEnrollmentPhotosValue {
  ON = 'on',
  OFF = 'off',
}

export enum AccountRecoveryEnabledValue {
  ON = 'true',
  OFF = 'false',
}

const BaseSettingsValues = {
  featureKey: 'icp',
  configType: 'security',
  categoryName: 'authn-control',
  valueType: 'value',
};

export const SettingsValues = { ...BaseSettingsValues, name: 'mfa-type' };

export enum FieldNames {
  USS_VALUE = 'USS_VALUE',
  IAM_VALUE = 'IAM_VALUE',
  USS_ONE_FACTOR_VALUE = 'USS_ONE_FACTOR_VALUE',
  IAM_ONE_FACTOR_VALUE = 'IAM_ONE_FACTOR_VALUE',
  USER_CAN_SHOW_LIVE_FEED_VALUE = 'USER_CAN_SHOW_LIVE_FEED_VALUE',
  DISCARD_ENROLLMENT_PHOTOS_VALUE = 'DISCARD_ENROLLMENT_PHOTOS_VALUE',
  ACCOUNT_RECOVERY_ENABLED_VALUE = 'ACCOUNT_RECOVERY_ENABLED_VALUE',
  STEP_UP_MFA_FREQUENCY = 'STEP_UP_MFA_FREQUENCY',
  STEP_UP_MFA_FREQUENCY_CUSTOM_HOURS = 'STEP_UP_MFA_FREQUENCY_CUSTOM_HOURS',
  STEP_UP_MFA_FREQUENCY_CUSTOM_MINUTES = 'STEP_UP_MFA_FREQUENCY_CUSTOM_MINUTES',
}

export interface FormValues {
  [FieldNames.USS_VALUE]: AuthnLevel;
  [FieldNames.IAM_VALUE]: AuthnLevel;
  [FieldNames.IAM_ONE_FACTOR_VALUE]: boolean;
  [FieldNames.USS_ONE_FACTOR_VALUE]: boolean;
  [FieldNames.USER_CAN_SHOW_LIVE_FEED_VALUE]: boolean;
  [FieldNames.DISCARD_ENROLLMENT_PHOTOS_VALUE]: boolean;
  [FieldNames.ACCOUNT_RECOVERY_ENABLED_VALUE]: boolean;
  [FieldNames.STEP_UP_MFA_FREQUENCY]?: number;
  [FieldNames.STEP_UP_MFA_FREQUENCY_CUSTOM_HOURS]?: number;
  [FieldNames.STEP_UP_MFA_FREQUENCY_CUSTOM_MINUTES]?: number;
}

export const getSettingsTemplate = {
  featureKey: BaseSettingsValues.featureKey,
  categoryName: BaseSettingsValues.categoryName,
};

export const updateSettingsTemplate = {
  featureKey: SettingsValues.featureKey,
  configType: SettingsValues.configType,
  categoryName: SettingsValues.categoryName,
  name: SettingsValues.name,
  valueType: SettingsValues.valueType,
};

export const UserCanShowLiveFeedSettingValues = {
  ...BaseSettingsValues,
  instanceKey: InstanceKey.AM_PORTAL,
  name: 'user-can-show-live-feed',
};

export const updateUserCanShowLiveFeedSettingsTemplate = {
  featureKey: UserCanShowLiveFeedSettingValues.featureKey,
  configType: UserCanShowLiveFeedSettingValues.configType,
  categoryName: UserCanShowLiveFeedSettingValues.categoryName,
  instanceKey: UserCanShowLiveFeedSettingValues.instanceKey,
  name: UserCanShowLiveFeedSettingValues.name,
  valueType: UserCanShowLiveFeedSettingValues.valueType,
};

export const DiscardEnrollmentPhotosSettingValues = {
  ...BaseSettingsValues,
  instanceKey: '',
  name: 'store-enrollment-photos-enabled',
};

export const updateDiscardEnrollmentPhotosSettingsTemplate = {
  featureKey: DiscardEnrollmentPhotosSettingValues.featureKey,
  configType: DiscardEnrollmentPhotosSettingValues.configType,
  categoryName: DiscardEnrollmentPhotosSettingValues.categoryName,
  instanceKey: DiscardEnrollmentPhotosSettingValues.instanceKey,
  name: DiscardEnrollmentPhotosSettingValues.name,
  valueType: DiscardEnrollmentPhotosSettingValues.valueType,
};

export const AccountRecoveryEnabledSettingValues = {
  ...BaseSettingsValues,
  name: 'account-recovery-enabled', // provisional key name pending LYRA-1884 confirmation
};

// account-recovery-enabled is read and written without an instanceKey.
export const updateAccountRecoveryEnabledSettingsTemplate = {
  featureKey: AccountRecoveryEnabledSettingValues.featureKey,
  configType: AccountRecoveryEnabledSettingValues.configType,
  categoryName: AccountRecoveryEnabledSettingValues.categoryName,
  name: AccountRecoveryEnabledSettingValues.name,
  valueType: AccountRecoveryEnabledSettingValues.valueType,
};
